import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grow,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Backdrop,
  Fade,
  Autocomplete,
  Avatar,
  Chip,
} from '@mui/material'
import {
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  GetApp as GetAppIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'
import AdvancedPagination from '../components/pagination/AdvancedPagination'
import api from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { Sync as SyncIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type Document = {
  _id: string
  title: string
  fileUrl: string
  fileKey: string
  patientId: string | null
  therapistId: string | null
  uploadedBy: string
  uploadedByRole: string
  documentType: string
  isDeleted: boolean
  createdAt: string
  updatedAt: string
  patientName?: string
  therapistName?: string
}

const DocumentsPage: React.FC = () => {
  const { isPatient, isTherapist } = useAuth()
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  )
  const [documentDetails, setDocumentDetails] = useState<any>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  })

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    file: null as File | null,
    patientId: null as string | null,
    therapistId: null as string | null,
    patientName: '',
    therapistName: '',
    documentType: 'general',
  })

  // Patient/Provider search state
  const [searchTerm, setSearchTerm] = useState('')
  const [options, setOptions] = useState<any[]>([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  const documentTypes = ['general', 'contract', 'session', 'note', 'poc']

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Date coming soon'
    const date = dayjs.utc(dateString).local()

    if (!date.isValid()) return 'Date coming soon'

    return date.format('MMM D, YYYY h:mm A')
  }

  const getFileNameFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      const fileName = pathname.split('/').pop() || 'document.pdf'
      // Remove query parameters from filename
      return fileName.split('?')[0]
    } catch {
      return 'document.pdf'
    }
  }

  const capitalizeDocumentType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1)
  }

  const getUploadedByText = (uploadedByRole: string) => {
    if (uploadedByRole === 'therapist') return 'Provider'
    if (uploadedByRole === 'patient') return 'Patient'
    return uploadedByRole.charAt(0).toUpperCase() + uploadedByRole.slice(1)
  }

  const canDeleteDocument = (document: Document) => {
    // Patients can only delete documents they uploaded
    if (isPatient) {
      return document.uploadedByRole !== 'therapist'
    }
    // Therapists can only delete documents they uploaded
    if (isTherapist) {
      return document.uploadedByRole !== 'patient'
    }
    return true
  }

  const fetchDocuments = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        limit: rowsPerPage,
      }

      if (searchQuery?.trim()) {
        params.search = searchQuery.trim()
      }

      if (typeFilter !== 'all') {
        params.type = typeFilter
      }

      const response = await api.get('/documents', { params })
      const responseData = response.data || {}

      const documentsArray = Array.isArray(responseData.data)
        ? responseData.data
        : []

      // Map documents and extract patient/provider names if available
      const mapped = documentsArray.map((doc: any) => {
        if (isPatient) {
          // For patients, show therapist/provider name
          return {
            ...doc,
            therapistName:
              doc.therapistId?.firstName && doc.therapistId?.lastName
                ? `${doc.therapistId.firstName} ${doc.therapistId.lastName}`
                : doc.therapistId?.email || 'Not Applicable',
          }
        } else {
          // For therapists, show patient name
          return {
            ...doc,
            patientName:
              doc.patientId?.firstName && doc.patientId?.lastName
                ? `${doc.patientId.firstName} ${doc.patientId.lastName}`
                : doc.patientId?.email || 'Not Applicable',
          }
        }
      })

      const total =
        typeof responseData.totalRecords === 'number'
          ? responseData.totalRecords
          : mapped.length

      setDocuments(mapped)
      setTotalRecords(total)
    } catch (fetchError) {
      console.error('Failed to load documents', fetchError)
      setError('Unable to load documents right now. Please try again later.')
      setDocuments([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, searchQuery, typeFilter])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
    setPage(0)
  }

  const handleTypeFilterChange = (event: any) => {
    setTypeFilter(event.target.value)
    setPage(0)
    // API will be called automatically via useEffect when typeFilter changes
  }

  const clearFilters = () => {
    setSearchQuery('')
    setTypeFilter('all')
    setPage(0)
  }

  const filteredDocuments = documents.filter(doc => {
    if (typeFilter !== 'all' && doc.documentType !== typeFilter) {
      return false
    }
    return true
  })

  // Fetch patients/providers for search
  const fetchOptions = useCallback(
    async (search: string) => {
      if (!search.trim()) {
        setOptions([])
        return
      }

      setLoadingOptions(true)
      try {
        const endpoint = isPatient
          ? '/contracts/patient-providers'
          : '/contracts/provider-patients'
        const response = await api.get(endpoint, {
          params: {
            search: search.trim(),
          },
        })
        const items = response.data?.data || response.data || []
        setOptions(items)
      } catch (error) {
        console.error(
          `Failed to fetch ${isPatient ? 'providers' : 'patients'}`,
          error
        )
        setOptions([])
      } finally {
        setLoadingOptions(false)
      }
    },
    [isPatient]
  )

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        fetchOptions(searchTerm)
      } else {
        setOptions([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm, fetchOptions])

  const handleOpenUploadDialog = () => {
    setUploadForm({
      title: '',
      file: null,
      patientId: null,
      therapistId: null,
      patientName: '',
      therapistName: '',
      documentType: 'general',
    })
    setSearchTerm('')
    setOptions([])
    setUploadDialogOpen(true)
  }

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false)
    setUploadForm({
      title: '',
      file: null,
      patientId: null,
      therapistId: null,
      patientName: '',
      therapistName: '',
      documentType: 'general',
    })
    setSearchTerm('')
    setOptions([])
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadForm({
        ...uploadForm,
        file: event.target.files[0],
      })
    }
  }

  const handleUploadDocument = async () => {
    if (!uploadForm.title.trim()) {
      setSnackbarState({
        open: true,
        message: 'Please enter a document title',
        severity: 'error',
      })
      return
    }

    if (!uploadForm.file) {
      setSnackbarState({
        open: true,
        message: 'Please select a file to upload',
        severity: 'error',
      })
      return
    }

    try {
      const formData = new FormData()
      formData.append('file', uploadForm.file)
      formData.append('title', uploadForm.title)
      formData.append('documentType', uploadForm.documentType)
      if (isPatient) {
        formData.append(
          'therapistId',
          uploadForm.therapistId ? uploadForm.therapistId : 'null'
        )
      } else {
        formData.append(
          'patientId',
          uploadForm.patientId ? uploadForm.patientId : 'null'
        )
      }

      await api.post('/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
      })

      setSnackbarState({
        open: true,
        message: 'Document uploaded successfully',
        severity: 'success',
      })
      handleCloseUploadDialog()
      fetchDocuments()
    } catch (uploadError) {
      console.error('Failed to upload document', uploadError)
      setSnackbarState({
        open: true,
        message: 'Failed to upload document. Please try again.',
        severity: 'error',
      })
    }
  }

  const handleOpenDeleteDialog = (document: Document) => {
    setSelectedDocument(document)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setSelectedDocument(null)
  }

  const handleDeleteDocument = async () => {
    if (!selectedDocument) return

    try {
      await api.delete(`/documents/${selectedDocument._id}`)
      setSnackbarState({
        open: true,
        message: 'Document deleted successfully',
        severity: 'success',
      })
      handleCloseDeleteDialog()
      fetchDocuments()
    } catch (deleteError) {
      console.error('Failed to delete document', deleteError)
      setSnackbarState({
        open: true,
        message: 'Failed to delete document. Please try again.',
        severity: 'error',
      })
    }
  }

  const handleDownload = (document: Document) => {
    if (document.fileUrl) {
      window.open(document.fileUrl, '_blank')
    }
  }

  const handleView = async (document: Document) => {
    setSelectedDocument(document)
    setDetailsDialogOpen(true)
    setLoadingDetails(true)
    setDocumentDetails(null)

    try {
      const response = await api.get(`/documents/detail/${document._id}`)
      const details = response.data?.data || response.data || null
      setDocumentDetails(details)
    } catch (error) {
      console.error('Failed to fetch document details', error)
      // Fallback to using the document data we already have
      setDocumentDetails(document)
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false)
    setSelectedDocument(null)
    setDocumentDetails(null)
  }

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return
    setSnackbarState(prev => ({ ...prev, open: false }))
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = parseInt(event.target.value, 10)
    setRowsPerPage(value)
    setPage(0)
  }

  const renderCardView = () => (
    <Grid container spacing={3}>
      {filteredDocuments.map((document, index) => (
        <Grow
          in
          key={document._id}
          timeout={300 + index * 100}
          style={{ transformOrigin: '0 0 0' }}
        >
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                {/* Title and Type */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="start"
                  gap={1}
                  mb={2}
                  flexWrap="wrap"
                >
                  <Typography
                    variant="h6"
                    component="div"
                    fontWeight={600}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    {document.title}
                  </Typography>
                  <Chip
                    label={capitalizeDocumentType(document.documentType)}
                    size="small"
                    color="primary"
                    sx={{
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>

                {/* File Name */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    display: 'block',
                    fontStyle: 'italic',
                  }}
                >
                  {getFileNameFromUrl(document.fileUrl)}
                </Typography>

                {/* Patient/Provider Info */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {isPatient
                      ? `Provider Name: ${(document as any).therapistName || 'Not Applicable'}`
                      : `Patient Name: ${document.patientName || 'Not Applicable'}`}
                  </Typography>
                </Box>

                {/* Date */}
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(document.createdAt)}
                  </Typography>
                </Box>

                {/* Uploaded By */}
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <InfoIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Uploaded by: {getUploadedByText(document.uploadedByRole)}
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box display="flex" gap={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleView(document)}
                    fullWidth
                  >
                    View Details
                  </Button>
                  <Tooltip placement="top" title="Download">
                    <IconButton
                      color="primary"
                      onClick={() => handleDownload(document)}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  {canDeleteDocument(document) && (
                    <Tooltip placement="top" title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(document)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grow>
      ))}
    </Grid>
  )

  const renderTableView = () => (
    <Grow in timeout={300}>
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Document Name
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Document Type
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  {isPatient ? 'Provider Name' : 'Patient Name'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Date
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={600}>
                  Uploaded By
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={600}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocuments.map((document, index) => (
              <Grow in key={document._id} timeout={200 + index * 50}>
                <TableRow
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {document.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {getFileNameFromUrl(document.fileUrl)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {capitalizeDocumentType(document.documentType)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {isPatient
                        ? (document as any).therapistName || 'Not Applicable'
                        : document.patientName || 'Not Applicable'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(document.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getUploadedByText(document.uploadedByRole)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip placement="top" title="View document">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleView(document)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip placement="top" title="Download">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(document)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canDeleteDocument(document) && (
                        <Tooltip placement="top" title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(document)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </Grow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grow>
  )

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}
        flexWrap="wrap"
        gap={2}
        width="100%"
      >
        {/* LEFT SIDE: Title + Description + Search */}
        <Box flex="1" minWidth="280px">
          <Typography
            variant="h4"
            gutterBottom
            color="primary"
            fontWeight={600}
          >
            Document Management
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {isPatient
              ? 'Manage your therapy documents, insurance forms, and medical records'
              : 'Manage therapy documents, insurance forms, and patient records'}
          </Typography>

          {/* SEARCH BAR JUST BELOW TITLE & DESCRIPTION */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              placeholder={
                isPatient
                  ? 'Search by Document Name, Provider Name'
                  : 'Search by Document Name, Patient Name'
              }
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: '350px', md: '500px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>All Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={handleTypeFilterChange}
                label="All Type"
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="all">All Type</MenuItem>
                {documentTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* <Button
              variant="outlined"
              color="primary"
              onClick={clearFilters}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                py: 1,
                px: 2.5,
                width: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
              }}
            >
              Clear Filters
            </Button> */}

            {/* Reset Filter Button */}
            <Box
              sx={{
                flex: {
                  xs: '1 1 100%',
                  sm: '0 0 auto',
                },
                minWidth: { xs: '100%', sm: 'auto' },
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<SyncIcon sx={{ fontSize: '18px' }} />}
                onClick={clearFilters}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: '0.625rem',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  px: 2.5,
                  py: 1,
                  height: '40px',
                  minWidth: '100px',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Box>

        {/* RIGHT SIDE: Upload Button + Toggle Buttons */}
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenUploadDialog}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Upload Document
          </Button>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="300px"
          gap={2}
        >
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary">
            Loading documents...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredDocuments.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          gap={2}
        >
          <Typography variant="h6" color="text.secondary">
            No documents found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery || typeFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Upload your first document to get started'}
          </Typography>
        </Box>
      ) : (
        <>
          <Box>
            {viewMode === 'grid' ? renderCardView() : renderTableView()}
          </Box>
          <Box mt={4} display="flex" justifyContent="flex-end">
            <AdvancedPagination
              count={totalRecords}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </>
      )}

      {/* Upload Document Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Upload New Document
          </Typography>
          <IconButton
            size="small"
            onClick={handleCloseUploadDialog}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: '1.5rem !important' }}>
          <Box display="flex" flexDirection="column" gap={3}>
            <TextField
              label="Document Title"
              value={uploadForm.title}
              onChange={e =>
                setUploadForm({ ...uploadForm, title: e.target.value })
              }
              fullWidth
              required
              placeholder="Progress Report"
            />

            <Box display="flex" alignItems="flex-start" gap={2}>
              <TextField
                label="Select File"
                value={uploadForm.file?.name || ''}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ flex: 1 }}
                required
              />
              <Button
                component="label"
                variant="outlined"
                size="medium"
                sx={{
                  minWidth: '120px',
                  mt: '8px',
                  borderRadius: 1,
                  textTransform: 'none',
                }}
              >
                Choose file
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </Button>
            </Box>

            <Autocomplete
              options={options}
              getOptionLabel={option => {
                if (typeof option === 'string') return option
                const firstName = option.firstName || ''
                const lastName = option.lastName || ''
                return `${firstName} ${lastName}`.trim() || option.email || ''
              }}
              loading={loadingOptions}
              value={
                isPatient
                  ? uploadForm.therapistId
                    ? options.find(p => p._id === uploadForm.therapistId) ||
                    null
                    : null
                  : uploadForm.patientId
                    ? options.find(p => p._id === uploadForm.patientId) || null
                    : null
              }
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  const firstName = newValue.firstName || ''
                  const lastName = newValue.lastName || ''
                  const fullName = `${firstName} ${lastName}`.trim()
                  if (isPatient) {
                    setUploadForm({
                      ...uploadForm,
                      therapistId: newValue._id,
                      therapistName: fullName || newValue.email || '',
                    })
                  } else {
                    setUploadForm({
                      ...uploadForm,
                      patientId: newValue._id,
                      patientName: fullName || newValue.email || '',
                    })
                  }
                } else {
                  if (isPatient) {
                    setUploadForm({
                      ...uploadForm,
                      therapistId: null,
                      therapistName: '',
                    })
                  } else {
                    setUploadForm({
                      ...uploadForm,
                      patientId: null,
                      patientName: '',
                    })
                  }
                }
              }}
              onInputChange={(event, newInputValue) => {
                setSearchTerm(newInputValue)
              }}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderOption={(props, option) => {
                const firstName = option.firstName || ''
                const lastName = option.lastName || ''
                const fullName = `${firstName} ${lastName}`.trim()
                const location = [option.city, option.state]
                  .filter(Boolean)
                  .join(', ')
                const initials =
                  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
                const displayName = isPatient
                  ? fullName || option.email || 'Unknown Provider'
                  : fullName || option.email || 'Unknown Patient'

                return (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 1.5,
                      px: 2,
                    }}
                  >
                    <Avatar
                      src={option.profilePhotoUrl}
                      alt={fullName}
                      sx={{ width: 40, height: 40 }}
                    >
                      {!option.profilePhotoUrl && initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {displayName}
                      </Typography>
                      {location && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {location}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={isPatient ? 'Provider Name' : 'Patient Name'}
                  placeholder={
                    isPatient
                      ? 'Search for a provider...'
                      : 'Search for a patient...'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingOptions ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={uploadForm.documentType}
                onChange={e =>
                  setUploadForm({
                    ...uploadForm,
                    documentType: e.target.value,
                  })
                }
                label="Document Type"
              >
                {documentTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleCloseUploadDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadDocument}
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Upload Document
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Document Details
          </Typography>
          <IconButton
            size="small"
            onClick={handleCloseDetailsDialog}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 0,
            py: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: 'hidden',
            flex: 1,
            minHeight: { md: '500px' },
          }}
        >
          {loadingDetails ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              minHeight="400px"
              width="100%"
            >
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              {/* Left Side - Document Details */}
              <Box
                sx={{
                  width: { xs: '100%', md: '40%' },
                  borderRight: { xs: 'none', md: '1px solid' },
                  borderBottom: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                  p: 3,
                  overflowY: 'auto',
                  bgcolor: 'grey.50',
                }}
              >
                {documentDetails && (
                  <Box display="flex" flexDirection="column" gap={3}>
                    {/* Title */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'grey.600',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.7rem',
                          mb: 0.75,
                          display: 'block',
                        }}
                      >
                        Document Title
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {documentDetails.title || selectedDocument?.title}
                      </Typography>
                    </Box>

                    {/* Document Type */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'grey.600',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.7rem',
                          mb: 0.75,
                          display: 'block',
                        }}
                      >
                        Document Type
                      </Typography>
                      <Chip
                        label={capitalizeDocumentType(
                          documentDetails.documentType ||
                          selectedDocument?.documentType ||
                          ''
                        )}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>

                    {/* Patient/Provider Info */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'grey.600',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.7rem',
                          mb: 0.75,
                          display: 'block',
                        }}
                      >
                        {isPatient ? 'Provider' : 'Patient'}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {isPatient
                          ? (documentDetails as any).therapistName ||
                          (selectedDocument as any)?.therapistName ||
                          'Not Applicable'
                          : documentDetails.patientName ||
                          selectedDocument?.patientName ||
                          'Not Applicable'}
                      </Typography>
                    </Box>

                    {/* Upload Date */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'grey.600',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.7rem',
                          mb: 0.75,
                          display: 'block',
                        }}
                      >
                        Upload Date
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {formatDate(
                            documentDetails.createdAt ||
                            selectedDocument?.createdAt
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Uploaded By */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'grey.600',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.7rem',
                          mb: 0.75,
                          display: 'block',
                        }}
                      >
                        Uploaded By
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {getUploadedByText(
                          documentDetails.uploadedByRole ||
                          selectedDocument?.uploadedByRole ||
                          ''
                        )}
                      </Typography>
                    </Box>

                    {/* File Name */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'grey.600',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '0.7rem',
                          mb: 0.75,
                          display: 'block',
                        }}
                      >
                        File Name
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: 'text.secondary',
                          wordBreak: 'break-all',
                        }}
                      >
                        {getFileNameFromUrl(
                          documentDetails.fileUrl ||
                          selectedDocument?.fileUrl ||
                          ''
                        )}
                      </Typography>
                    </Box>

                    {/* Download Button */}
                    {/* {(documentDetails?.fileUrl ||
                      selectedDocument?.fileUrl) && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<GetAppIcon />}
                        onClick={() => {
                          const url =
                            documentDetails?.fileUrl ||
                            selectedDocument?.fileUrl
                          if (url) {
                            window.open(url, '_blank')
                          }
                        }}
                        fullWidth
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        Download Document
                      </Button>
                    )} */}
                  </Box>
                )}
              </Box>

              {/* Right Side - Document Viewer */}
              <Box
                sx={{
                  width: { xs: '100%', md: '60%' },
                  height: { xs: '400px', md: '100%' },
                  minHeight: { md: '500px' },
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {documentDetails?.fileUrl || selectedDocument?.fileUrl ? (
                  <Box
                    sx={{
                      width: '100vw',
                      height: '90vh',
                      overflow: 'hidden',
                      bgcolor: 'white',
                      boxShadow: 2,
                    }}
                  >
                    <iframe
                      src={
                        documentDetails?.fileUrl || selectedDocument?.fileUrl
                      }
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                      title="Document Viewer"
                    />
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                  >
                    <DescriptionIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                    <Typography variant="body2" color="text.secondary">
                      Document preview not available
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleCloseDetailsDialog}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Delete Document
          </Typography>
          <IconButton
            size="small"
            onClick={handleCloseDeleteDialog}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: '1.5rem !important' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this document?
          </Typography>
          {selectedDocument && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {selectedDocument.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {capitalizeDocumentType(selectedDocument.documentType)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDocument}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarState.severity}
          sx={{ width: '100%' }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DocumentsPage
